import React, { useCallback } from 'react';
import * as env from 'config/environment';
import { AssetStuffing } from './assetStuffing/AssetStuffing';
import { TotalAssetStuffingFluctuationList } from './assetStuffing/TotalAssetStuffingFluctuationList';
import { GroupAssetStuffingRegisteredAccountList } from './assetStuffing/GroupAssetStuffingRegisteredAccountList';
import { PortfolioStuffing } from './PortfolioStuffing/PortfolioStuffing';
import { ProfitAndlossStuffing } from './ProfitAndLossStuffing/ProfitAndLossStuffing';
import { PortfolioRatioStuffing } from './PortfolioRatioStuffing/PortfolioRatioStuffing';
import { AssetBundleType, StuffingType } from 'components/features/home/Home';
import { GetUserAssetResult, GetUserBrokerResultItem } from 'config/api';
import { isPremium } from '../utils/userStatus';

export const LineNoticeBanner = ({ className }: { className: string[] }) => {
  return (
    <>
      {/* PC */}
      <div className={`hidden lg:flex justify-center ${className[0]}`}>
        <div className={`${className[1]}`}>
          <a id="side_line" href={env.lineAddFriendURL}>
            <img
              src="/static/images/line2.png"
              alt="友だち追加"
              className="mx-auto"
            />
          </a>
        </div>
      </div>

      {/* SP */}
      <div className={`flex lg:hidden justify-center ${className[0]}`}>
        <div className={`${className[2]}`}>
          <a id="side_line" href={env.lineAddFriendURL}>
            <img
              src="/static/images/line2.png"
              alt="友だち追加"
              className="mx-auto"
            />
          </a>
        </div>
      </div>
    </>
  );
};

type StuffingDomainProps = {
  assetBundleType: AssetBundleType;
  stuffingType: StuffingType;
  asset: GetUserAssetResult;
  brokersInfo: GetUserBrokerResultItem[];
  UserBrokerUpdateTime: React.ReactElement;
  isLinkedLine: boolean;
  getOutOfDateAccount: (item: {
    account: string;
  }) => { account: string } | null;
  toPreview: (elemnet: React.ReactElement) => void;
  backHome: () => void;
};

type StuffingProps = StuffingDomainProps & Record<string, unknown>;

const Stuffing = ({
  stuffingType,
  assetBundleType,
  asset,
  brokersInfo,
  UserBrokerUpdateTime,
  isLinkedLine,
  getOutOfDateAccount,
  backHome,
  toPreview,
}: StuffingProps): React.ReactElement | null => {
  const getIsOutOfDate = useCallback(v => !!getOutOfDateAccount(v), [
    getOutOfDateAccount,
  ]);

  switch (stuffingType) {
    case '資産':
      return (
        <AssetStuffing
          {...{
            asset,
            UserBrokerUpdateTime,
            assetBundleType,
            toPreview,
            backHome,
          }}>
          <>
            {!isLinkedLine && isPremium() && (
              <LineNoticeBanner {...{ className: ['', 'w-1/2', 'w-3/4'] }} />
            )}
            {assetBundleType.type === '資産総額' ? (
              <TotalAssetStuffingFluctuationList key={assetBundleType.type} />
            ) : (
              <GroupAssetStuffingRegisteredAccountList
                {...{
                  getOutOfDateAccount,
                  assetBundleType,
                  brokers: brokersInfo,
                }}
                key={assetBundleType.type}
              />
            )}
          </>
        </AssetStuffing>
      );
    case '保有資産':
      return (
        <PortfolioStuffing
          asset={asset}
          brokersInfo={brokersInfo}
          UserBrokerUpdateTime={UserBrokerUpdateTime}
          assetBundleType={assetBundleType}
          getIsOutOfDate={getIsOutOfDate}
          backHome={backHome}
          toPreview={toPreview}
        />
      );
    case '損益':
      if (assetBundleType.type === 'グループ資産') return null;
      return (
        <ProfitAndlossStuffing
          {...{
            asset,
            assetBundleType,
            UserBrokerUpdateTime,
            getIsOutOfDate,
            toPreview,
            backHome,
          }}
        />
      );
    case 'ポートフォリオ':
      return (
        <PortfolioRatioStuffing
          {...{
            brokersInfo,
            UserBrokerUpdateTime,
            asset,
            toPreview,
            backHome,
            assetBundleType,
            getIsOutOfDate,
          }}
        />
      );
  }
};

type HomeContentsInternalProps = {
  cardType: AssetBundleType;
  Tab: (props: {
    stuffingType: StuffingType;
    setStuffingType: React.Dispatch<React.SetStateAction<StuffingType>>;
  }) => React.ReactElement | null;
  stuffingType: StuffingType;
  asset: GetUserAssetResult;
  setStuffingType: React.Dispatch<React.SetStateAction<StuffingType>>;
  brokers: GetUserBrokerResultItem[];
  UserBrokerUpdateTime: React.ReactElement;
  isLinkedLine: boolean;
  getOutOfDateAccount: (item: {
    account: string;
  }) => { account: string } | null;
  toPreview: (elemnet: React.ReactElement) => void;
  backHome: () => void;
};

export const HomeContentsInternal = ({
  cardType,
  Tab,
  stuffingType,
  asset,
  setStuffingType,
  isLinkedLine,
  brokers,
  UserBrokerUpdateTime,
  getOutOfDateAccount,
  backHome,
  toPreview,
}: HomeContentsInternalProps): React.ReactElement | null => {
  return (
    <>
      <div className="pt-7 bg-bluegray-600"></div>
      <div className="">
        <Tab stuffingType={stuffingType} setStuffingType={setStuffingType} />
        <Stuffing
          {...{
            assetBundleType: cardType,
            stuffingType,
            asset,
            brokersInfo: brokers,
            UserBrokerUpdateTime,
            isLinkedLine,
            getOutOfDateAccount,
            backHome,
            toPreview,
          }}
        />
      </div>
    </>
  );
};
